// La placa del mes: el resumen de un mes cerrado, para mandar al grupo.
//
// Mismo estilo y mismas herramientas que la de la noche (placa.ts). Se dibuja
// dos veces: la primera en un lienzo alto de sobra para medir hasta dónde
// llega, la segunda en uno del alto justo. Así el alto no se calcula a mano
// sección por sección, que con textos que ocupan uno o dos renglones se
// desfasa enseguida.

import type { PlacaMes } from '../services/api';
import { LOGROS } from '../nucleo/logros';
import { NOMBRE_APP } from '../marca';
import { cargarSimbolo, simboloEnCabecera } from './marcaPlaca';
import {
  ANCHO,
  APAGADO,
  aPng,
  BAJA,
  BLANCO,
  cargarFuentes,
  CELESTE,
  fondo,
  MARGEN,
  seccion,
  TEXTO,
  TITULO,
} from './placa';

const NAVY = '#002C5B';
const CAJA = 'rgba(255,255,255,0.07)';

/**
 * Los colores del podio van en el BORDE del recuadro y en el número del
 * puesto, no de relleno: pedido por Marcos, para que no quede cargado.
 */
const MEDALLA: Record<number, string> = { 1: '#E6B84A', 2: '#C9D1D9', 3: '#C7834A' };
/** El relleno de los recuadros del podio: un toque del metal, o nada. */
const RELLENO_MEDALLA = false;

function rellenoDe(posicion: number): string {
  if (!RELLENO_MEDALLA) return CAJA;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(MEDALLA[posicion]!.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},0.12)`;
}
const UTIL = ANCHO - 2 * MARGEN;

/** Cuántos renglones de logros entran, y hasta cuántos nombres se listan en uno. */
const MAX_LOGROS = 8;
const MAX_NOMBRES = 6;

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

export async function dibujarPlacaMes(
  datos: PlacaMes,
  link: string,
  apagadas: ReadonlySet<string> = new Set(),
): Promise<Blob> {
  const [, simbolo] = await Promise.all([cargarFuentes(), cargarSimbolo()]);

  const borrador = document.createElement('canvas');
  borrador.width = ANCHO;
  borrador.height = 5000;
  const alto = dibujar(borrador.getContext('2d')!, datos, link, null, apagadas, simbolo);

  const lienzo = document.createElement('canvas');
  lienzo.width = ANCHO;
  lienzo.height = Math.max(alto, 1350);
  dibujar(lienzo.getContext('2d')!, datos, link, lienzo.height, apagadas, simbolo);
  return aPng(lienzo);
}

/** Dibuja todo y devuelve el alto que ocupó. `alto` null = pasada para medir. */
function dibujar(
  c: CanvasRenderingContext2D,
  d: PlacaMes,
  link: string,
  alto: number | null,
  apagadas: ReadonlySet<string>,
  simbolo: HTMLImageElement | null,
): number {
  const va = (seccion: string) => !apagadas.has(seccion);
  fondo(c, alto ?? c.canvas.height);
  const [anio, numMes] = d.mes.split('-').map(Number) as [number, number];

  // ── Cabecera ──
  let y = 120;
  const inicioMarca = simboloEnCabecera(c, simbolo, MARGEN, y);
  texto(c, `${NOMBRE_APP.toUpperCase()} · RESUMEN DEL MES`, inicioMarca, y, `48px ${TITULO}`, CELESTE);
  y += 140;
  texto(c, d.titulo.toUpperCase(), MARGEN, y, `150px ${TITULO}`, BLANCO, UTIL);
  y += 62;
  if (d.desde && d.hasta) {
    const dia = (f: string) => Number(f.slice(8));
    const noches = d.numeros.noches === 1 ? '1 noche' : `${d.numeros.noches} noches`;
    texto(c, `Del ${dia(d.desde)} al ${dia(d.hasta)} de ${MESES[numMes - 1]} · ${noches}`, MARGEN, y, `32px ${TEXTO}`, APAGADO);
  }
  if (!d.definitivo) cinta(c, 'PROVISORIA · EL MES NO CERRÓ');

  // ── El campeón ──
  const campeones = d.podio.filter((f) => f.posicion === 1);
  const resto = va('podio') ? d.podio.filter((f) => f.posicion > 1).slice(0, 2) : [];
  y += 56;
  if (campeones.length > 0) y = tarjetaCampeon(c, campeones, d, y);

  // ── 2º y 3º ──
  if (resto.length > 0) {
    y += 24;
    const ancho = (UTIL - 24) / 2;
    resto.forEach((f, i) => {
      const x = MARGEN + i * (ancho + 24);
      caja(c, x, y, ancho, 196, 22, rellenoDe(f.posicion), MEDALLA[f.posicion]);
      texto(c, `${f.posicion}º`, x + 28, y + 58, `44px ${TITULO}`, MEDALLA[f.posicion] ?? CELESTE);
      texto(c, f.nombre.toUpperCase(), x + 28, y + 124, `66px ${TITULO}`, BLANCO, ancho - 56);
      texto(c, `${f.puntos} pts · ${f.ganados}-${f.perdidos} · ${conSigno(f.delta_elo)}`, x + 28, y + 170, `30px ${TEXTO}`, APAGADO, ancho - 56);
    });
    y += 196;
  }

  // ── Reconocimientos ──
  if (va('reconocimientos')) y = reconocimientos(c, d, campeones, y);

  // ── El mes en números ──
  if (va('numeros')) y = numeros(c, d, y);

  // ── Pareja del mes ──
  if (d.pareja && va('pareja')) {
    y += 90;
    y = seccion(c, 'PAREJA DEL MES', y);
    y = renglonDoble(c, '', d.pareja.nombres.join(' · '), `${d.pareja.ganados}-${d.pareja.perdidos}`, CELESTE, y, true);
  }

  // ── Destacados ──
  const destacados = va('destacados') ? armarDestacados(d) : [];
  if (destacados.length > 0) {
    y += 70;
    y = seccion(c, 'DESTACADOS', y);
    for (const renglon of destacados) y = parrafo(c, renglon, y) + 14;
  }

  // ── Logros del mes ──
  const logros = va('logros') ? ordenarLogros(d.logros, d.catalogo_logros ?? []).slice(0, MAX_LOGROS) : [];
  if (logros.length > 0) {
    y += 56;
    y = seccion(c, 'LOGROS DEL MES', y);
    for (const { logro, nombres, veces } of logros) {
      // Los de humor, igual que los demás: el humor también es parte del grupo.
      const trozos: Trozo[] = [[`${logro.emoji} ${logro.nombre}${veces > 1 ? ` ×${veces}` : ''}`, BLANCO]];
      if (nombres.length <= MAX_NOMBRES) trozos.push([` · ${nombres.map(pegado).join(', ')}`, APAGADO]);
      y = parrafo(c, trozos, y) + 12;
    }
  }

  // ── Pie ──
  y += 70;
  texto(c, link.replace(/^https?:\/\//, '').replace(/\/$/, ''), MARGEN, y, `28px ${TEXTO}`, APAGADO);
  const siguiente = numMes === 12 ? `ENERO ${anio + 1}` : MESES[numMes]!.toUpperCase();
  c.textAlign = 'right';
  texto(c, `${siguiente} ARRANCA DE CERO`, ANCHO - MARGEN, y, `36px ${TITULO}`, CELESTE);
  c.textAlign = 'left';
  return y + 60;
}

function reconocimientos(
  c: CanvasRenderingContext2D,
  d: PlacaMes,
  campeones: PlacaMes['podio'],
  y: number,
): number {
  y += 90;
  y = seccion(c, 'RECONOCIMIENTOS', y);
  if (d.mas_subio.length > 0) {
    y = renglonDoble(c, '📈 El que más subió', lista(d.mas_subio.map((p) => p.nombre)), conSigno(d.mas_subio[0]!.delta_elo), CELESTE, y);
  }
  if (d.mas_presente.length > 0) {
    const n = d.mas_presente[0]!.partidos;
    y = renglonDoble(c, '🙋 El más presente', lista(d.mas_presente.map((p) => p.nombre)), `${n} partidos`, APAGADO, y);
  }
  const unoSolo = (xs: Array<{ nombre: string }>) => (xs.length === 1 ? xs[0]!.nombre : null);
  const quien = unoSolo(campeones);
  if (quien && quien === unoSolo(d.mas_subio) && quien === unoSolo(d.mas_presente)) {
    texto(c, 'Se llevó los tres del mes.', MARGEN, y, `30px ${TEXTO}`, APAGADO);
    y += 50;
  }
  return y;
}

function numeros(c: CanvasRenderingContext2D, d: PlacaMes, y: number): number {
  y += 70;
  y = seccion(c, 'EL MES EN NÚMEROS', y) - 40;
  const cuadros: Array<[number, string]> = [
    [d.numeros.partidos, 'partidos'],
    [d.numeros.noches, 'noches'],
    [d.numeros.jugadores, 'jugadores'],
    [d.numeros.tiebreaks, 'tiebreaks'],
  ];
  const anchoNum = (UTIL - 3 * 14) / 4;
  cuadros.forEach(([n, que], i) => {
    const x = MARGEN + i * (anchoNum + 14);
    caja(c, x, y, anchoNum, 150, 20, CAJA);
    c.textAlign = 'center';
    texto(c, String(n), x + anchoNum / 2, y + 92, `92px ${TITULO}`, BLANCO);
    texto(c, que, x + anchoNum / 2, y + 132, `28px ${TEXTO}`, APAGADO);
    c.textAlign = 'left';
  });
  return y + 150;
}

function armarDestacados(d: PlacaMes): Trozo[][] {
  const destacados: Trozo[][] = [];
  if (d.racha) {
    destacados.push([
      ['🚀 ', BLANCO],
      [lista(d.racha.nombres), BLANCO, true],
      [` ganó ${d.racha.largo} seguidos`, BLANCO],
    ]);
  }
  if (d.siempre) {
    const n = d.siempre.nombres.length;
    destacados.push([
      ['📅 ', BLANCO],
      [n === 1 ? pegado(d.siempre.nombres[0]!) : String(n), BLANCO, true],
      [n === 1 ? ` vino las ${d.siempre.noches} noches` : ` vinieron las ${d.siempre.noches} noches`, BLANCO],
      ...(n === 1 ? [] : ([[` · ${d.siempre.nombres.map(pegado).join(', ')}`, APAGADO]] as Trozo[])),
    ]);
  }
  for (const p of d.mas_peleado) {
    destacados.push([
      ['🎲 ', BLANCO],
      [d.mas_peleado.length > 1 ? 'Los más peleados: ' : 'El más peleado: ', BLANCO],
      [`${p.ganadores.map(pegado).join(' · ')} ${p.set}`, BLANCO, true],
      [` a ${p.perdedores.map(pegado).join(' · ')}`, BLANCO],
      [` (${diaMes(p.fecha)}, tiebreak ${p.tiebreak})`, APAGADO],
    ]);
  }
  return destacados;
}

function tarjetaCampeon(
  c: CanvasRenderingContext2D,
  campeones: PlacaMes['podio'],
  d: PlacaMes,
  y: number,
): number {
  const f = campeones[0]!;
  // El 1: un chip adentro de la tarjeta si es el mismo campeón; si no, un
  // renglón aparte debajo (lo tiene otro, o quedó vacante).
  const el1 = d.el_1;
  const nombres = campeones.map((x) => x.nombre);
  let chip: string | null = null;
  if (el1.nombre && nombres.includes(el1.nombre)) {
    chip =
      el1.cambio === 'primero'
        ? `👑 ${nombres.length > 1 ? `${el1.nombre.toUpperCase()}: ` : ''}PRIMER DUEÑO DE “EL 1”`
        : el1.cambio === 'nuevo'
          ? `👑 ${nombres.length > 1 ? `${el1.nombre.toUpperCase()}: ` : ''}NUEVO “EL 1”`
          : `👑 ${nombres.length > 1 ? `${el1.nombre.toUpperCase()} ` : ''}SIGUE SIENDO “EL 1”`;
  }
  const alto = chip ? 330 : 262;

  caja(c, MARGEN, y, UTIL, alto, 28, rellenoDe(1), MEDALLA[1]);
  texto(c, campeones.length > 1 ? '👑 CAMPEONES DEL MES' : '👑 CAMPEÓN DEL MES', MARGEN + 40, y + 70, `44px ${TITULO}`, MEDALLA[1]!);
  texto(c, nombres.join(' Y ').toUpperCase(), MARGEN + 40, y + 180, `120px ${TITULO}`, BLANCO, UTIL - 80);
  const datos = `${f.puntos} puntos · ${f.ganados} ganados y ${f.perdidos} perdidos`;
  texto(c, datos, MARGEN + 40, y + 232, `600 36px ${TEXTO}`, BLANCO);
  if (campeones.length === 1) {
    const ancho = c.measureText(datos).width;
    texto(c, ` · ${conSigno(f.delta_elo)} de nivel`, MARGEN + 40 + ancho, y + 232, `36px ${TEXTO}`, APAGADO);
  }
  if (chip) {
    c.font = `40px ${TITULO}`;
    const ancho = c.measureText(chip).width + 40;
    caja(c, MARGEN + 40, y + 256, ancho, 52, 12, BLANCO);
    texto(c, chip, MARGEN + 60, y + 297, `40px ${TITULO}`, NAVY);
  }
  y += alto;

  const aparte =
    el1.cambio === 'vacante'
      ? '👑 “EL 1” QUEDÓ VACANTE: HAY EMPATE ARRIBA'
      : el1.nombre && !chip && (el1.cambio === 'nuevo' || el1.cambio === 'primero')
        ? `👑 NUEVO “EL 1”: ${el1.nombre.toUpperCase()}`
        : null;
  if (aparte) {
    texto(c, aparte, MARGEN, y + 70, `52px ${TITULO}`, BLANCO, UTIL);
    y += 90;
  }
  return y;
}

/**
 * Los logros con su ficha del catálogo: primero los que más salieron. Los de
 * humor van mezclados con los demás, sin tratamiento aparte (pedido por Marcos).
 */
function ordenarLogros(logros: PlacaMes['logros'], dinamicos: PlacaMes['catalogo_logros']) {
  const catalogo = dinamicos.length ? dinamicos : LOGROS;
  return logros
    .map((l) => ({ ...l, logro: catalogo.find((x) => x.id === l.logro_id) }))
    .filter((l): l is typeof l & { logro: NonNullable<typeof l.logro> } => l.logro !== undefined)
    .sort(
      (a, b) =>
        b.veces - a.veces ||
        catalogo.indexOf(a.logro) - catalogo.indexOf(b.logro),
    );
}

// ── Herramientas de dibujo ──

/** Un pedazo de un párrafo: texto, color y si va en negrita. */
type Trozo = [string, string, boolean?];

function texto(c: CanvasRenderingContext2D, t: string, x: number, y: number, fuente: string, color: string, max?: number) {
  c.font = fuente;
  c.fillStyle = color;
  c.fillText(t, x, y, max);
}

/**
 * Texto corrido con partes de distinto color, cortado en renglones por
 * palabra. Devuelve el `y` del último renglón.
 */
function parrafo(c: CanvasRenderingContext2D, trozos: Trozo[], y: number, alto = 46): number {
  const tam = 34;
  let x = MARGEN;
  for (const [t, color, negrita] of trozos) {
    c.font = `${negrita ? '600 ' : ''}${tam}px ${TEXTO}`;
    c.fillStyle = color;
    // Se corta solo en espacios comunes (los de adentro de un nombre son
    // duros, ver `pegado`), y se conservan como palabras propias para que
    // "· " no se pegue a lo que sigue.
    for (const palabra of t.split(/( +)/)) {
      if (!palabra) continue;
      const ancho = c.measureText(palabra).width;
      if (x + ancho > ANCHO - MARGEN && x > MARGEN) {
        if (/^ +$/.test(palabra)) continue;
        y += alto;
        x = MARGEN;
      }
      if (x === MARGEN && /^ +$/.test(palabra)) continue;
      c.fillText(palabra, x, y);
      x += ancho;
    }
  }
  return y + alto;
}

/** Etiqueta a la izquierda; nombre y valor a la derecha. */
function renglonDoble(
  c: CanvasRenderingContext2D,
  etiqueta: string,
  nombre: string,
  valor: string,
  colorValor: string,
  y: number,
  nombreALaIzquierda = false,
): number {
  if (etiqueta) texto(c, etiqueta, MARGEN, y, `34px ${TEXTO}`, BLANCO);
  c.textAlign = 'right';
  texto(c, valor, ANCHO - MARGEN, y, `600 38px ${TEXTO}`, colorValor);
  const anchoValor = c.measureText(valor).width;
  if (nombreALaIzquierda) {
    c.textAlign = 'left';
    texto(c, nombre, MARGEN, y, `600 36px ${TEXTO}`, BLANCO, UTIL - anchoValor - 30);
  } else {
    texto(c, nombre, ANCHO - MARGEN - anchoValor - 16, y, `600 36px ${TEXTO}`, BLANCO, UTIL / 2);
    c.textAlign = 'left';
  }
  return y + 64;
}

function caja(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  relleno: string,
  borde?: string,
) {
  c.beginPath();
  c.roundRect(x, y, w, h, r);
  c.fillStyle = relleno;
  c.fill();
  if (borde) {
    c.lineWidth = 3;
    c.strokeStyle = borde;
    c.stroke();
  }
}

/** Una cinta en diagonal en la esquina de arriba a la derecha. */
function cinta(c: CanvasRenderingContext2D, t: string) {
  c.save();
  c.translate(ANCHO - 150, 110);
  c.rotate(Math.PI / 5);
  c.fillStyle = BAJA;
  c.fillRect(-340, -30, 680, 56);
  c.textAlign = 'center';
  texto(c, t, 0, 12, `34px ${TITULO}`, NAVY);
  c.restore();
}

function conSigno(n: number): string {
  return n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '=';
}

/** '2026-09-25' -> '25/9' */
function diaMes(fecha: string): string {
  return `${Number(fecha.slice(8))}/${Number(fecha.slice(5, 7))}`;
}

/** Un nombre que no se parte entre renglones: "Marcos F." no puede quedar "Marcos / F.". El reemplazo es un espacio duro (U+00A0). */
function pegado(nombre: string): string {
  return nombre.replace(/ /g, ' ');
}

/** ["A"] -> "A" · ["A","B"] -> "A y B" */
function lista(nombres: string[]): string {
  nombres = nombres.map(pegado);
  if (nombres.length <= 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}
